import { useCallback, useEffect } from 'react'
import { useJourneyStore } from '@/store/journeyStore'
import { useDockBridgeStore } from '@/store/dockBridgeStore'
import { useProjectStore } from '@/store/projectStore'
import { useVisiblePolling } from '@/hooks/useVisiblePolling'
import type { JourneyTab } from '@/api/journey'
import AnalyticsTab from '../AnalyticsTab'
import PrototypeTab from '../PrototypeTab'
import AppSettingsPage from '../settings/AppSettingsPage'
import NextStepTab from './NextStepTab'
import NextStepStrip from './NextStepStrip'
import PlanTab from './PlanTab'
import PeopleTab from './PeopleTab'
import GoLiveTab from './GoLiveTab'
import HubTab from './HubTab'
import AdvancedHome from './AdvancedHome'
import AdvancedSub from './AdvancedSub'
import './simple.css'

/**
 * The simple Workbench — ONE screen that always shows the next thing to do
 * (docs/plans/simple-builder-redesign.md). Which tabs exist comes from the
 * App type's journey (backend data/journeys/*.json); this component only
 * draws the tab the URL names, with the next-step strip on every tab but
 * Next step itself.
 */
export default function SimpleWorkbench({ tab, sub }: { tab: string; sub?: string }) {
  const project = useProjectStore((s) => s.currentProject?.application.folder_name || '')
  const { journey, refresh } = useJourneyStore()
  const agentStatus = useDockBridgeStore((s) => (s.project === project ? s.agentStatus : ''))

  const reload = useCallback(() => { if (project) void refresh(project) }, [project, refresh])
  useEffect(() => { reload() }, [reload])

  // Anything the agent (or a designer) wrote can move the next step.
  useEffect(() => {
    const onChanged = (e: Event) => {
      const p = (e as CustomEvent).detail?.project
      if (!p || p === project) reload()
    }
    window.addEventListener('twasta:agent-files-changed', onChanged as EventListener)
    return () => window.removeEventListener('twasta:agent-files-changed', onChanged as EventListener)
  }, [project, reload])
  // The dock's status changing (paused, review, finished) moves it too.
  useEffect(() => { if (agentStatus) reload() }, [agentStatus, reload])
  const busy = agentStatus === 'running' || journey?.agent.status === 'running' || !!journey?.tests_running
  useVisiblePolling(reload, busy ? 8000 : 60000, !!project)

  const def: JourneyTab | undefined =
    journey?.tabs.find((t) => t.id === tab) || journey?.tab_catalog?.find((t) => t.id === tab)

  const body = (() => {
    if (tab === 'advanced') return sub ? <AdvancedSub sub={sub} /> : <AdvancedHome />
    if (tab === 'settings') return <AppSettingsPage />
    if (!def) {
      // Before the journey answers, the two tabs every journey has can draw.
      if (tab === 'next') return <NextStepTab />
      if (tab === 'screens') return <PrototypeTab />
      return journey ? <NextStepTab /> : <div className="spinner" style={{ margin: '3rem auto' }} />
    }
    switch (def.view) {
      case 'next': return <NextStepTab />
      case 'plan': return <PlanTab />
      case 'objects': return <PrototypeTab key={def.id} tab={def} />
      case 'analytics': return <AnalyticsTab />
      case 'people': return <PeopleTab tab={def} />
      case 'golive': return <GoLiveTab />
      case 'hub': return <HubTab tab={def} />
      default: return <NextStepTab />
    }
  })()

  const fills = tab === 'advanced' && !!sub
    || def?.view === 'objects' || def?.view === 'analytics' || (!def && tab === 'screens')
  return (
    <div className="swb" style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1, minHeight: 0 }}>
      {tab !== 'next' && def?.view !== 'next' && <NextStepStrip />}
      <div style={{ display: 'flex', flexDirection: 'column', flex: fills ? 1 : undefined, minHeight: 0 }}>
        {body}
      </div>
    </div>
  )
}
